import * as Sentry from '@sentry/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { attrs } from './primitives';
import { isMacPlatform } from './utils';

/** For showing ⌘ or Ctrl in shortcuts. */
export function useIsMac(): boolean {
    return useMemo(() => isMacPlatform(), []);
}

/** While the user navigates by keyboard, hover is off; moving the mouse turns it back on. */
export function useKeyboardNavigation(): {
    isKeyboardNavigating: boolean;
    setIsKeyboardNavigating: React.Dispatch<React.SetStateAction<boolean>>;
    handleKeyboardNavigation: (e: React.KeyboardEvent) => void;
    handleMouseMove: () => void;
} {
    const [isKeyboardNavigating, setIsKeyboardNavigating] = useState(false);

    const handleKeyboardNavigation = useCallback((e: React.KeyboardEvent) => {
        if (['ArrowDown', 'ArrowUp', 'Tab'].includes(e.key)) {
            setIsKeyboardNavigating(true);
        }
    }, []);

    const handleMouseMove = useCallback(() => {
        setIsKeyboardNavigating(false);
    }, []);

    return {
        isKeyboardNavigating,
        setIsKeyboardNavigating,
        handleKeyboardNavigation,
        handleMouseMove,
    };
}

const METRIC_PREFIX = 'command_palette';

/** Strips a trailing UUID, so dynamic command IDs don't blow up Sentry's attribute cardinality. */
const UUID_SUFFIX = /-[0-9a-f]{8}(-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i;
function normalizeCommandId(id: string): string {
    return id.replace(UUID_SUFFIX, '');
}

/** Gives the palette's attributes their own namespace in Sentry's flat attribute list. */
function prefixAttributes(
    record: Record<string, string | number | boolean> | undefined,
): Record<string, string | number | boolean> | undefined {
    if (!record) return undefined;
    const prefixed: Record<string, string | number | boolean> = {};
    for (const [key, value] of Object.entries(record)) {
        prefixed[`attribute.${key}`] = value;
    }
    return prefixed;
}

export type PaletteEventKind =
    /** With `trigger`. */
    | 'opened'
    /** With `method`. */
    | 'closed'
    /** With `query`. */
    | 'searched'
    /** With `command_id`, `command_group`, `open_in_new_tab`. */
    | 'command_executed'
    /** With `command_id`, `command_group`. */
    | 'command_succeeded'
    /** With `command_id`, `command_group`. */
    | 'command_failed'
    /** With `method`. */
    | 'navigated_back'
    /** With `command_id`. */
    | 'navigated_to_subcommands'
    /** With `command_id`. */
    | 'quick_look_opened'
    /** With `command_id`, `method`. */
    | 'quick_look_closed'
    /** With `source`. */
    | 'selection_cleared';

export type PaletteEventAttrs = {
    /** E.g. 'button' or 'keyboard'. */
    trigger?: string;
    /** How the palette closed, e.g. 'escape', 'button' or 'programmatic'. */
    method?: string;
    commandId?: string;
    commandGroup?: string | null;
    openInNewTab?: boolean;
    /** Only for 'searched' events. */
    query?: string;
    [key: string]: string | number | boolean | null | undefined;
};

type PaletteAnalyticsOptions = {
    /** Sent with every metric, e.g. table_id or rfq_id. */
    telemetry?: Record<string, string | number | boolean>;
    paletteType: 'global' | 'table';
};

type PaletteAnalyticsReturn = {
    openedAtRef: React.MutableRefObject<number>;
    trackEvent: (kind: PaletteEventKind, eventAttrs?: PaletteEventAttrs) => void;
};

/** Counts `command_palette.<event>` metrics in Sentry, with every attribute prefixed `attribute.`. */
export function usePaletteAnalytics({ telemetry, paletteType }: PaletteAnalyticsOptions): PaletteAnalyticsReturn {
    const openedAtRef = useRef<number>(0);

    const trackEvent = useCallback(
        (kind: PaletteEventKind, eventAttrs?: PaletteEventAttrs) => {
            const normalizedAttrs: Record<string, string | number | boolean> = {
                palette_type: paletteType,
            };

            if (eventAttrs) {
                for (const [key, value] of Object.entries(eventAttrs)) {
                    if (value === undefined) continue;

                    const snakeKey = key.replace(/([A-Z])/g, '_$1').toLowerCase();

                    if (snakeKey === 'command_group' && value === null) {
                        normalizedAttrs[snakeKey] = 'ungrouped';
                    } else if (snakeKey === 'command_id' && typeof value === 'string') {
                        normalizedAttrs[snakeKey] = normalizeCommandId(value);
                    } else if (value !== null) {
                        normalizedAttrs[snakeKey] = value as string | number | boolean;
                    }
                }
            }

            const allAttrs = prefixAttributes(attrs(telemetry, normalizedAttrs));
            const baseAttrs = prefixAttributes(attrs(telemetry, { palette_type: paletteType }));

            switch (kind) {
                case 'opened':
                    openedAtRef.current = Date.now();
                    Sentry.metrics.count(`${METRIC_PREFIX}.opened`, 1, { attributes: allAttrs });
                    break;

                case 'closed':
                    Sentry.metrics.count(`${METRIC_PREFIX}.closed`, 1, { attributes: allAttrs });
                    Sentry.metrics.distribution(
                        `${METRIC_PREFIX}.session_duration_ms`,
                        Date.now() - openedAtRef.current,
                        { attributes: baseAttrs, unit: 'millisecond' },
                    );
                    break;

                default:
                    Sentry.metrics.count(`${METRIC_PREFIX}.${kind}`, 1, { attributes: allAttrs });
                    break;
            }
        },
        [telemetry, paletteType],
    );

    return { openedAtRef, trackEvent };
}

/** Calls `onOpen` and `resetState` each time `open` turns true. */
export function usePaletteOpenEffect(open: boolean, onOpen: () => void, resetState: () => void): void {
    const prevOpenRef = useRef(open);

    useEffect(() => {
        if (open && !prevOpenRef.current) {
            onOpen();
            resetState();
        }
        prevOpenRef.current = open;
    }, [open, onOpen, resetState]);
}
